import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Mic } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirect = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (user) return <Navigate to={user.role === "super_admin" ? "/admin/platform" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email.trim(), password);
      if (u?.must_reset_password) navigate("/change-password");
      else if (u?.role === "super_admin") navigate("/admin/platform");
      else navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="pgm-login-bg">
      <div className="pgm-reveal" style={{ width: "100%", maxWidth: 460 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, background: "var(--pgm-magenta)", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid var(--pgm-wine)" }}>
            <Mic size={28} color="var(--pgm-cream)" />
          </div>
          <div>
            <div className="pgm-display" style={{ fontSize: 30, lineHeight: 1 }}>ShowRunner</div>
            <div className="pgm-display" style={{ fontSize: 30, lineHeight: 1, color: "var(--pgm-magenta)" }}>HQ</div>
          </div>
        </div>

        <div className="pgm-card" style={{ borderLeftWidth: 4 }}>
          <div className="pgm-micro" style={{ marginBottom: 4 }}>◆ Sign in</div>
          <h2 className="pgm-display" style={{ fontSize: 28, marginBottom: 18 }}>Welcome back</h2>

          <button
            type="button"
            onClick={startGoogleAuth}
            className="pgm-btn pgm-btn-ghost"
            style={{ width: "100%", justifyContent: "center", padding: "12px 18px", background: "var(--pgm-paper)" }}
            data-testid="login-google-btn"
          >
            <GoogleIcon /> Continue with Google
          </button>

          <div className="pgm-divider" style={{ margin: "18px 0" }}>or use email</div>

          <form onSubmit={submit} className="pgm-reveal-stagger" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label className="pgm-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pgm-input"
                required
                autoFocus
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <label className="pgm-label">Password</label>
                <Link to="/forgot-password" className="pgm-link" style={{ fontSize: 11 }} data-testid="login-forgot-link">Forgot?</Link>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pgm-input"
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="pgm-callout" style={{ background: "var(--pgm-orange)", color: "var(--pgm-cream)" }} data-testid="login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="pgm-btn" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <div className="pgm-divider">◆</div>
          <p style={{ fontSize: 12, color: "var(--pgm-wine)", opacity: 0.7 }}>
            Admin accounts are created by the Super Admin. <Link to="/" className="pgm-link">Request access</Link> if you don't have one yet.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}
